/**
 * mockTradingData.ts
 * Development-only mock data for strategies, playbooks, criteria, confluences, and entry models
 *
 * Used by the Trade creation drawer during WU02 development in lieu of real database records.
 * Real Strategy/Playbook CRUD settings are built in a separate work unit (S01-WU7A).
 * This file is the single source of truth for all mock trading entity IDs — Quick Trade
 * presets referencing these IDs will resolve correctly as long as this file is present.
 *
 * DO NOT use in production code paths. Conditionally replaced once real settings data
 * is wired into the trade drawer (future WU).
 */

import type { StrategySnapshot, PlaybookSnapshot, CriteriaSnapshot, ConfluenceSnapshot, EntryModelSnapshot } from '@/types/shared/StrategySnapshots';
import type { ConfluencePolarity } from '@/types/shared/ConfluencePolarity';
import type { LiveTradingSettings } from '@/types/schema/LiveTradingSettings';
import type { ReasonCategorySnapshot, ReasonSnapshot } from '@/types/shared/RiskSnapshots';

// ===== MOCK STRATEGY IDS =====

export const MOCK_STRATEGY_ICT = 'mock-strategy-ict' as const;
export const MOCK_STRATEGY_SMC = 'mock-strategy-smc' as const;

// ===== MOCK PLAYBOOK IDS =====

export const MOCK_PB_ICT_FVG = 'mock-pb-ict-fvg' as const;
export const MOCK_PB_ICT_OTE = 'mock-pb-ict-ote' as const;
export const MOCK_PB_ICT_BOS = 'mock-pb-ict-bos' as const;
export const MOCK_PB_SMC_CHOCH = 'mock-pb-smc-choch' as const;
export const MOCK_PB_SMC_DZ = 'mock-pb-smc-dz' as const;

// ===== STRATEGY SNAPSHOTS =====

export const MOCK_STRATEGIES: readonly StrategySnapshot[] = [
  {
    strategyId: MOCK_STRATEGY_ICT,
    name: 'ICT Concepts',
    description: 'Inner Circle Trader methodology — liquidity, fair value gaps, and market structure',
    philosophy: 'Price seeks liquidity and fills imbalances. Trade with smart money, not against it.',
  },
  {
    strategyId: MOCK_STRATEGY_SMC,
    name: 'SMC Methodology',
    description: 'Smart Money Concepts — order blocks, change of character, and demand/supply zones',
    philosophy: 'Follow institutional order flow by identifying where smart money accumulates positions.',
  },
] as const;

// ===== PLAYBOOK SNAPSHOTS (extended with linkage data for form population) =====

export interface MockPlaybookData {
  snapshot: PlaybookSnapshot;
  strategyId: string;
  criteriaIds: string[];
  confluenceLinks: Array<{ confluenceId: string; polarity: ConfluencePolarity }>;
  entryModelIds: string[];
}

export const MOCK_PLAYBOOKS: readonly MockPlaybookData[] = [
  // ── ICT: FVG Retest ──────────────────────────────────────────────────
  {
    snapshot: {
      playbookId: MOCK_PB_ICT_FVG,
      name: 'FVG Retest',
      description: 'Enter on a retest of a fair value gap in the direction of the higher timeframe trend',
    },
    strategyId: MOCK_STRATEGY_ICT,
    criteriaIds: ['mock-c-ict-fvg-1', 'mock-c-ict-fvg-2', 'mock-c-ict-fvg-3', 'mock-c-ict-fvg-4'],
    confluenceLinks: [
      // Positive (12) — tests scroll threshold on the positive column
      { confluenceId: 'mock-cf-htf-trend', polarity: 'positive' },
      { confluenceId: 'mock-cf-session-killzone', polarity: 'positive' },
      { confluenceId: 'mock-cf-liquidity-swept', polarity: 'positive' },
      { confluenceId: 'mock-cf-ob-confluence', polarity: 'positive' },
      { confluenceId: 'mock-cf-daily-bias', polarity: 'positive' },
      { confluenceId: 'mock-cf-pdh-pdl', polarity: 'positive' },
      { confluenceId: 'mock-cf-displacement', polarity: 'positive' },
      { confluenceId: 'mock-cf-inducement', polarity: 'positive' },
      { confluenceId: 'mock-cf-market-open', polarity: 'positive' },
      { confluenceId: 'mock-cf-volume-spike', polarity: 'positive' },
      { confluenceId: 'mock-cf-equal-hl', polarity: 'positive' },
      { confluenceId: 'mock-cf-fib-confluence', polarity: 'positive' },
      // Negative (11) — tests scroll threshold on the negative column
      { confluenceId: 'mock-cf-news-imminent', polarity: 'negative' },
      { confluenceId: 'mock-cf-spread-wide', polarity: 'negative' },
      { confluenceId: 'mock-cf-counter-trend', polarity: 'negative' },
      { confluenceId: 'mock-cf-low-volume', polarity: 'negative' },
      { confluenceId: 'mock-cf-choppy', polarity: 'negative' },
      { confluenceId: 'mock-cf-near-resistance', polarity: 'negative' },
      { confluenceId: 'mock-cf-earnings', polarity: 'negative' },
      { confluenceId: 'mock-cf-gap-unfilled', polarity: 'negative' },
      { confluenceId: 'mock-cf-opposing-liq', polarity: 'negative' },
      { confluenceId: 'mock-cf-htf-bearish', polarity: 'negative' },
      { confluenceId: 'mock-cf-asia-expanded', polarity: 'negative' },
    ],
    entryModelIds: ['mock-em-market', 'mock-em-limit', 'mock-em-stop-limit'],
  },
  // ── ICT: OTE Setup ──────────────────────────────────────────────────
  {
    snapshot: {
      playbookId: MOCK_PB_ICT_OTE,
      name: 'OTE Setup',
      description: 'Optimal trade entry using 61.8–79% Fibonacci retracement in a trending market',
    },
    strategyId: MOCK_STRATEGY_ICT,
    criteriaIds: ['mock-c-ict-ote-1', 'mock-c-ict-ote-2', 'mock-c-ict-ote-3'],
    confluenceLinks: [
      { confluenceId: 'mock-cf-htf-trend', polarity: 'positive' },
      { confluenceId: 'mock-cf-liquidity-swept', polarity: 'positive' },
      { confluenceId: 'mock-cf-news-imminent', polarity: 'negative' },
      { confluenceId: 'mock-cf-spread-wide', polarity: 'negative' },
    ],
    entryModelIds: ['mock-em-limit', 'mock-em-stop-limit'],
  },
  // ── ICT: BOS Continuation ───────────────────────────────────────────
  {
    snapshot: {
      playbookId: MOCK_PB_ICT_BOS,
      name: 'BOS Continuation',
      description: 'Enter after a confirmed break of structure in the direction of the prevailing trend',
    },
    strategyId: MOCK_STRATEGY_ICT,
    criteriaIds: ['mock-c-ict-bos-1', 'mock-c-ict-bos-2', 'mock-c-ict-bos-3'],
    confluenceLinks: [
      { confluenceId: 'mock-cf-htf-trend', polarity: 'positive' },
      { confluenceId: 'mock-cf-session-killzone', polarity: 'positive' },
      { confluenceId: 'mock-cf-news-imminent', polarity: 'negative' },
      { confluenceId: 'mock-cf-spread-wide', polarity: 'negative' },
    ],
    entryModelIds: ['mock-em-market', 'mock-em-limit', 'mock-em-stop-limit'],
  },
  // ── SMC: CHoCH Reversal ─────────────────────────────────────────────
  {
    snapshot: {
      playbookId: MOCK_PB_SMC_CHOCH,
      name: 'CHoCH Reversal',
      description: 'Change of character reversal — enter after structure flip confirmed with a retest',
    },
    strategyId: MOCK_STRATEGY_SMC,
    criteriaIds: ['mock-c-smc-choch-1', 'mock-c-smc-choch-2', 'mock-c-smc-choch-3', 'mock-c-smc-choch-4'],
    confluenceLinks: [
      { confluenceId: 'mock-cf-ob-confluence', polarity: 'positive' },
      { confluenceId: 'mock-cf-session-killzone', polarity: 'positive' },
      { confluenceId: 'mock-cf-news-imminent', polarity: 'negative' },
      { confluenceId: 'mock-cf-htf-trend', polarity: 'positive' },
      { confluenceId: 'mock-cf-spread-wide', polarity: 'negative' },
    ],
    entryModelIds: ['mock-em-market', 'mock-em-limit'],
  },
  // ── SMC: Demand Zone Bounce ─────────────────────────────────────────
  {
    snapshot: {
      playbookId: MOCK_PB_SMC_DZ,
      name: 'Demand Zone Bounce',
      description: 'Long entry from a confirmed institutional demand zone with order block confluence',
    },
    strategyId: MOCK_STRATEGY_SMC,
    criteriaIds: ['mock-c-smc-dz-1', 'mock-c-smc-dz-2', 'mock-c-smc-dz-3'],
    confluenceLinks: [
      { confluenceId: 'mock-cf-ob-confluence', polarity: 'positive' },
      { confluenceId: 'mock-cf-htf-trend', polarity: 'positive' },
      { confluenceId: 'mock-cf-news-imminent', polarity: 'negative' },
      { confluenceId: 'mock-cf-spread-wide', polarity: 'negative' },
    ],
    entryModelIds: ['mock-em-limit', 'mock-em-stop-limit', 'mock-em-market'],
  },
] as const satisfies readonly MockPlaybookData[];

// ===== CRITERIA SNAPSHOTS (flat lookup — checked: false by default) =====

export const MOCK_CRITERIA: readonly CriteriaSnapshot[] = [
  // ICT FVG criteria
  { criteriaId: 'mock-c-ict-fvg-1', name: 'FVG identified on entry timeframe', checked: false },
  { criteriaId: 'mock-c-ict-fvg-2', name: 'Price reacting to FVG midpoint or edge', checked: false },
  { criteriaId: 'mock-c-ict-fvg-3', name: 'HTF trend aligned', checked: false },
  { criteriaId: 'mock-c-ict-fvg-4', name: 'Liquidity sweep confirmed before entry', checked: false },
  // ICT OTE criteria
  { criteriaId: 'mock-c-ict-ote-1', name: 'Swing high/low clearly defined', checked: false },
  { criteriaId: 'mock-c-ict-ote-2', name: 'Price in 61.8–79% retracement zone', checked: false },
  { criteriaId: 'mock-c-ict-ote-3', name: 'Momentum confirmation on entry timeframe', checked: false },
  // ICT BOS criteria
  { criteriaId: 'mock-c-ict-bos-1', name: 'Break of structure confirmed on ETF', checked: false },
  { criteriaId: 'mock-c-ict-bos-2', name: 'Pullback to broken structure level', checked: false },
  { criteriaId: 'mock-c-ict-bos-3', name: 'Volume or spread confirms move', checked: false },
  // SMC CHoCH criteria
  { criteriaId: 'mock-c-smc-choch-1', name: 'Higher high / lower low broken (CHoCH)', checked: false },
  { criteriaId: 'mock-c-smc-choch-2', name: 'Retest of broken structure level', checked: false },
  { criteriaId: 'mock-c-smc-choch-3', name: 'Order block present at retest zone', checked: false },
  { criteriaId: 'mock-c-smc-choch-4', name: 'No competing imbalance above entry', checked: false },
  // SMC Demand Zone criteria
  { criteriaId: 'mock-c-smc-dz-1', name: 'Demand zone identified (strong move away)', checked: false },
  { criteriaId: 'mock-c-smc-dz-2', name: 'Price entering zone with reduced momentum', checked: false },
  { criteriaId: 'mock-c-smc-dz-3', name: 'HTF bias supports long entry', checked: false },
] as const satisfies readonly CriteriaSnapshot[];

// ===== CONFLUENCE SNAPSHOTS (shared pool — polarity set per-playbook at apply time) =====

/**
 * Flat confluence pool. When populating the drawer, use the polarity from
 * `MOCK_PLAYBOOKS[n].confluenceLinks` rather than a fixed value here.
 * The `polarity` field here is a placeholder — it will be overridden by the
 * playbook-specific polarity at apply time.
 */
export const MOCK_CONFLUENCES: readonly ConfluenceSnapshot[] = [
  // Positive
  { confluenceId: 'mock-cf-htf-trend', name: 'HTF trend aligned', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-session-killzone', name: 'Active session killzone', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-liquidity-swept', name: 'Liquidity swept before entry', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-ob-confluence', name: 'Order block confluence', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-daily-bias', name: 'Daily bias confirmed', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-pdh-pdl', name: 'PDH / PDL level confluence', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-displacement', name: 'Displacement candle present', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-inducement', name: 'Inducement leg formed', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-market-open', name: 'NY / London market open run', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-volume-spike', name: 'Volume spike on displacement', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-equal-hl', name: 'Equal highs / lows targeted', polarity: 'positive', checked: false },
  { confluenceId: 'mock-cf-fib-confluence', name: 'Fibonacci confluence at entry', polarity: 'positive', checked: false },
  // Negative
  { confluenceId: 'mock-cf-news-imminent', name: 'High-impact news within 15 min', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-spread-wide', name: 'Spread wider than normal', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-counter-trend', name: 'Counter-trend trade', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-low-volume', name: 'Low volume / pre-market hours', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-choppy', name: 'Choppy / ranging price action', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-near-resistance', name: 'Near major resistance level', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-earnings', name: 'Earnings report within 48 h', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-gap-unfilled', name: 'Overnight gap unfilled above', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-opposing-liq', name: 'Strong opposing liquidity nearby', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-htf-bearish', name: 'HTF structure bearish', polarity: 'negative', checked: false },
  { confluenceId: 'mock-cf-asia-expanded', name: 'Asia range expanded aggressively', polarity: 'negative', checked: false },
] as const satisfies readonly ConfluenceSnapshot[];

// ===== ENTRY MODEL SNAPSHOTS =====

export const MOCK_ENTRY_MODELS: readonly EntryModelSnapshot[] = [
  { entryModelId: 'mock-em-market', name: 'Market order', description: 'Immediate fill at current price', checked: false },
  { entryModelId: 'mock-em-limit', name: 'Limit order', description: 'Pending order at a specific price level', checked: false },
  { entryModelId: 'mock-em-stop-limit', name: 'Stop-limit order', description: 'Pending order triggered at stop, filled at limit', checked: false },
] as const satisfies readonly EntryModelSnapshot[];

// ===== NOTE CATEGORY MOCK DATA =====

/**
 * Development-only mock note categories — mirrors what a user would configure in
 * Live Trading Settings. Used as a fallback when `noteCategories` from settings is empty,
 * so the Notes & Images section in the TradeDrawer is testable during WU02 development.
 */
export const MOCK_NOTE_CATEGORIES = [
  { emoji: '👁', name: 'Observation', color: '#6366f1', showOnDashboard: true, showOnTradeEntry: true },
  { emoji: '⭐', name: 'Setup Quality', color: '#f59e0b', showOnDashboard: true, showOnTradeEntry: true },
  { emoji: '⚠️', name: 'Risk Note', color: '#ef4444', showOnDashboard: false, showOnTradeEntry: true },
  { emoji: '🧠', name: 'Psychology', color: '#8b5cf6', showOnDashboard: true, showOnTradeEntry: true },
] as const satisfies ReadonlyArray<LiveTradingSettings['noteCategories'][number]>;

// ===== REASON CATEGORY + REASON MOCK DATA =====

/** Reason data for mock — snapshot plus the category it belongs to */
export interface MockReasonData {
  snapshot: ReasonSnapshot;
  categoryId: string;
}

/**
 * Mock reason categories — mirrors what a user would configure for "Log as Missed" opportunity capture.
 * Covers the most common causes of missed setups in live trading.
 */
export const MOCK_REASON_CATEGORIES = [
  { categoryId: 'mock-rc-execution', name: 'Execution', description: 'Issues with trade execution process' },
  { categoryId: 'mock-rc-psychology', name: 'Psychology', description: 'Emotional or psychological barriers' },
  { categoryId: 'mock-rc-risk', name: 'Risk Management', description: 'Risk/reward did not meet plan criteria' },
  { categoryId: 'mock-rc-market', name: 'Market Conditions', description: 'Adverse or unsuitable market conditions' },
] as const satisfies readonly ReasonCategorySnapshot[];

/**
 * Mock reasons — grouped by category ID.
 * Use {@link getMockReasonsForCategory} to filter by category.
 */
export const MOCK_REASONS = [
  // Execution
  { snapshot: { reasonId: 'mock-r-missed-entry', name: 'Missed entry window', description: 'Price moved past ideal entry before order was placed' }, categoryId: 'mock-rc-execution' },
  { snapshot: { reasonId: 'mock-r-platform-issue', name: 'Platform / connection issue', description: 'Technical problem prevented order execution' }, categoryId: 'mock-rc-execution' },
  { snapshot: { reasonId: 'mock-r-size-unavailable', name: 'Insufficient position size available', description: 'Account or margin did not support the planned size' }, categoryId: 'mock-rc-execution' },
  // Psychology
  { snapshot: { reasonId: 'mock-r-hesitation', name: 'Hesitation', description: 'Failed to act despite a clear signal' }, categoryId: 'mock-rc-psychology' },
  { snapshot: { reasonId: 'mock-r-fear-loss', name: 'Fear of loss', description: 'Did not enter due to recent drawdown or loss streak' }, categoryId: 'mock-rc-psychology' },
  { snapshot: { reasonId: 'mock-r-overconfidence', name: 'Overconfidence override', description: 'Deviated from plan due to biased market read' }, categoryId: 'mock-rc-psychology' },
  // Risk Management
  { snapshot: { reasonId: 'mock-r-rr-below', name: 'R:R below minimum', description: 'Risk-reward ratio did not meet plan criteria' }, categoryId: 'mock-rc-risk' },
  { snapshot: { reasonId: 'mock-r-daily-limit', name: 'Daily loss limit reached', description: 'Max daily drawdown rule prevented new entries' }, categoryId: 'mock-rc-risk' },
  // Market Conditions
  { snapshot: { reasonId: 'mock-r-news', name: 'High-impact news event', description: 'Upcoming news made market conditions unsuitable' }, categoryId: 'mock-rc-market' },
  { snapshot: { reasonId: 'mock-r-spread', name: 'Spread too wide', description: 'Spread was significantly above normal range' }, categoryId: 'mock-rc-market' },
  { snapshot: { reasonId: 'mock-r-choppy', name: 'Choppy / no directional trend', description: 'Price action lacked sufficient directional conviction' }, categoryId: 'mock-rc-market' },
] as const satisfies readonly MockReasonData[];

// ===== LOOKUP HELPERS =====

/** Get mock playbook data by playbook ID */
export function getMockPlaybook(playbookId: string): MockPlaybookData | undefined {
  return MOCK_PLAYBOOKS.find(p => p.snapshot.playbookId === playbookId);
}

/** Get all mock playbooks for a given strategy ID */
export function getMockPlaybooksForStrategy(strategyId: string): readonly MockPlaybookData[] {
  return MOCK_PLAYBOOKS.filter(p => p.strategyId === strategyId);
}

/** Get mock criteria for a given playbook ID */
export function getMockCriteriaForPlaybook(playbookId: string): readonly CriteriaSnapshot[] {
  const pb = getMockPlaybook(playbookId);
  if (!pb) return [];
  return MOCK_CRITERIA.filter(c => pb.criteriaIds.includes(c.criteriaId));
}

/** Get mock confluence snapshots for a given playbook ID (with correct polarity applied) */
export function getMockConfluencesForPlaybook(playbookId: string): readonly ConfluenceSnapshot[] {
  const pb = getMockPlaybook(playbookId);
  if (!pb) return [];
  return pb.confluenceLinks.map(link => {
    const base = MOCK_CONFLUENCES.find(c => c.confluenceId === link.confluenceId);
    if (!base) return null;
    return { ...base, polarity: link.polarity };
  }).filter((c): c is ConfluenceSnapshot => c !== null);
}

/** Get mock entry model snapshots for a given playbook ID */
export function getMockEntryModelsForPlaybook(playbookId: string): readonly EntryModelSnapshot[] {
  const pb = getMockPlaybook(playbookId);
  if (!pb) return [];
  return MOCK_ENTRY_MODELS.filter(em => pb.entryModelIds.includes(em.entryModelId));
}

/** Get mock reasons filtered by category ID */
export function getMockReasonsForCategory(categoryId: string): readonly MockReasonData[] {
  return MOCK_REASONS.filter((r) => r.categoryId === categoryId);
}
